import { useEffect, useMemo, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useDebouncedCallback } from 'use-debounce';
import LayoutProvider from '../../Contexts/LayoutProvider';
import LibModal from '../../features/LibModal/LibModal';
import PageTitle from '../../utils/PageTitle';
import { mkRelativeUrl } from '../../utils/URL';
import SupportTicketHeader from './Components/SupportTicketHeader';
import SupportTicketHeaderMobile from './Components/SupportTicketHeaderMobile';
import SupportTicketMobile from './Components/SupportTicketMobile';
import SupportTicketTable from './Components/SupportTicketTable';
import AddIssueTicketModal from './Modals/AddIssueTicketModal';
import UpdateIssueTicketModal from './Modals/UpdateIssueTicketModal';
import SupportTicketProvider, { useSupportTicket } from './SupportTicketProvider';

function PageContent() {
    const { setParams, supportTicketData, handleReload, closeModal, modalType, modalData } =
        useSupportTicket();

    const navigate = useNavigate();
    // Search Params
    const [searchParams] = useSearchParams();
    const [queryStr, setQueryStr] = useState({ q: searchParams.get('q') || '' });
    const p = useMemo(
        () => ({
            q: searchParams.get('q') || '',
            department: searchParams.get('department') || '',
            status: searchParams.get('status') || '',
            'order-by': searchParams.get('order-by'),
            sort: searchParams.get('sort'),
            page: searchParams.get('page')
        }),
        [searchParams]
    );

    const debounceUpdate = useDebouncedCallback((val) => {
        navigate(mkRelativeUrl('/support-ticket', { ...p, q: val }));
    }, 400);

    const handleChangeFilterData = (e) => {
        const { name, value } = e.target;
        if (name) {
            if (name === 'q') {
                setQueryStr({ q: value });
                debounceUpdate(value);
            } else {
                navigate(mkRelativeUrl('/support-ticket', { ...p, [name]: value }));
            }
        }
    };

    const handlePagination = (page) => {
        navigate(mkRelativeUrl('/support-ticket', { ...p, page }));
    };

    // For mobile view reset filter
    const onResetFilter = () => {
        navigate(mkRelativeUrl('/support-ticket', { status: '', q: '' }));
    };

    const handleSortingData = (column) => {
        navigate(
            mkRelativeUrl('/support-ticket', {
                ...p,
                'order-by': column,
                sort: p.sort === 'desc' ? 'asc' : 'desc'
            })
        );
    };

    useEffect(() => {
        setParams(p);
    }, [p, setParams]);

    return (
        <div className="clients_page inner_page_cont over">
            <LayoutProvider>
                <SupportTicketHeader
                    layout="default"
                    handleChangeFilterData={handleChangeFilterData}
                    p={{ ...p, ...queryStr }}
                />
                <SupportTicketHeaderMobile
                    layout={['md', 'sm', 'xs', 'xxs']}
                    onResetFilter={onResetFilter}
                    handleChangeFilterData={handleChangeFilterData}
                    p={{ ...p, ...queryStr }}
                />
            </LayoutProvider>

            <LayoutProvider>
                <SupportTicketTable
                    layout="default"
                    data={supportTicketData}
                    handleSortingData={handleSortingData}
                    sortBy={p.sort}
                    sortedColumn={p['order-by']}
                    onChangePage={handlePagination}
                />
                <SupportTicketMobile layout={['md', 'sm', 'xs', 'xxs']} data={supportTicketData} />
            </LayoutProvider>

            {modalType === 'add-issue' && (
                <LibModal title="Add Issue Ticket" setOpen={closeModal}>
                    <AddIssueTicketModal closeModal={closeModal} handleReload={handleReload} />
                </LibModal>
            )}

            {modalType === 'update-issue' && (
                <LibModal title="Update Issue Ticket" setOpen={closeModal}>
                    <UpdateIssueTicketModal
                        closeModal={closeModal}
                        issueData={modalData}
                        handleReload={handleReload}
                    />
                </LibModal>
            )}
        </div>
    );
}

function SupportTicketPage() {
    // set page title
    PageTitle(`Support Ticket`);

    return (
        <SupportTicketProvider>
            <PageContent />
        </SupportTicketProvider>
    );
}

export default SupportTicketPage;
